import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    ForwardedRef,
} from 'react';
import { Card, Checkbox, Input } from '../../../../../shared/custom-ui';

interface XrayFormProps {
    defaultValues?: Record<string, any>;
}

const XrayForm = forwardRef((props: XrayFormProps, ref: ForwardedRef<any>) => {
    const { defaultValues = {} } = props;
    const [xrayObj, setXrayObj] = useState<Record<string, any>>({});
    const [isPrefilled, setIsPrefilled] = useState(false);

    useEffect(() => {
        if (!isPrefilled && Object.keys(defaultValues).length > 0) {
            setXrayObj(prev => ({ ...prev, ...defaultValues }));
            setIsPrefilled(true);
        }
    }, [defaultValues, isPrefilled]);

    useImperativeHandle(ref, () => ({
        getData: () => xrayObj,
    }));

    const handleCheckboxChange = (key: string) => {
        setXrayObj(prev => ({
            ...prev,
            [key]: !(prev[key] === true || prev[key] === 'true'),
        }));
    };

    const handleInputChange = (key: string, value: string | null) => {
        setXrayObj(prev => {
            if (value) {
                return {
                    ...prev,
                    [key]: value,
                };
            } else {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
        });
    };

    return (
        <Card title="X-RAY & FLUOROSCOPY" padding="small" variant="default" >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 py-4">
                <Checkbox
                    label="Abdomen*"
                    checked={xrayObj.abdomen === 'true' || xrayObj.abdomen === true}
                    onChange={() => handleCheckboxChange("abdomen")}
                />
                <div className="flex flex-col sm:flex-row sm:space-x-4">
                    <Checkbox
                        label="1 view*"
                        checked={xrayObj.one_view === 'true' || xrayObj.one_view === true}
                        onChange={() => handleCheckboxChange('one_view')}
                    />
                    <Checkbox
                        label="2 views*"
                        checked={xrayObj.two_views === 'true' || xrayObj.two_views === true}
                        onChange={() => handleCheckboxChange('two_views')}
                    />
                    <Checkbox
                        label="3 views*"
                        checked={xrayObj.three_views === 'true' || xrayObj.three_views === true}
                        onChange={() => handleCheckboxChange('three_views')}
                    />
                </div>
                <Checkbox
                    label="Chest*"
                    checked={xrayObj.chest === 'true' || xrayObj.chest === true}
                    onChange={() => handleCheckboxChange('chest')}
                />
                <Checkbox
                    label="Paranasal sinuses*"
                    checked={xrayObj.paranasal_sinuses === 'true' || xrayObj.paranasal_sinuses === true}
                    onChange={() => handleCheckboxChange('paranasal_sinuses')}
                />
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4">
                    <Checkbox
                        label="Rib with PA chest*"
                        checked={xrayObj.rib_with_pa_chest === 'true' || xrayObj.rib_with_pa_chest === true}
                        onChange={() => handleCheckboxChange('rib_with_pa_chest')}
                    />
                    <div className="flex space-x-4 mt-2 sm:mt-0">
                        <Checkbox
                            label="Both"
                            checked={xrayObj.rib_both === 'true' || xrayObj.rib_both === true}
                            onChange={() => handleCheckboxChange('rib_both')}
                        />
                        <Checkbox
                            label="L"
                            checked={xrayObj.rib_left === 'true' || xrayObj.rib_left === true}
                            onChange={() => handleCheckboxChange('rib_left')}
                        />
                        <Checkbox
                            label="R"
                            checked={xrayObj.rib_right === 'true' || xrayObj.rib_right === true}
                            onChange={() => handleCheckboxChange('rib_right')}
                        />
                    </div>
                </div>
                <div className="sm:col-span-2">
                    <Checkbox
                        label="Skull*"
                        checked={xrayObj.skull === 'true' || xrayObj.skull === true}
                        onChange={() => handleCheckboxChange('skull')}
                    />
                </div>
                <Checkbox
                    label="Scoliosis exam*"
                    checked={xrayObj.scoliosis_exam === 'true' || xrayObj.scoliosis_exam === true}
                    onChange={() => handleCheckboxChange('scoliosis_exam')}
                />
                <Checkbox
                    label="Spine* - Cervical*"
                    checked={xrayObj.spine_cervical === 'true' || xrayObj.spine_cervical === true}
                    onChange={() => handleCheckboxChange('spine_cervical')}
                />
                <Checkbox
                    label="Spine* - Thoracic*"
                    checked={xrayObj.spine_thoracic === 'true' || xrayObj.spine_thoracic === true}
                    onChange={() => handleCheckboxChange('spine_thoracic')}
                />
                <Checkbox
                    label="Spine* - Lumbosacral*"
                    checked={xrayObj.spine_lumbosacral === 'true' || xrayObj.spine_lumbosacral === true}
                    onChange={() => handleCheckboxChange('spine_lumbosacral')}
                />
                <div className="flex flex-col sm:flex-row items-start md:items-center sm:space-x-4">
                    <Checkbox
                        label="Joints* & extremities (specify)"
                        className="md:mt-4"
                        checked={xrayObj.joints_extremities === 'true' || xrayObj.joints_extremities === true}
                        onChange={() => handleCheckboxChange('joints_extremities')}
                    />
                    <Input
                        placeholder="Specify"
                        value={xrayObj.joints_extremities_specify || ''}
                        onChange={e => handleInputChange('joints_extremities_specify', e.target.value)}
                    />
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4">
                    <Checkbox
                        label="Barium enema"
                        checked={xrayObj.barium_enema === 'true' || xrayObj.barium_enema === true}
                        onChange={() => handleCheckboxChange('barium_enema')}
                    />
                    <Checkbox
                        label="with air-contrast"
                        checked={xrayObj.barium_air_contrast === 'true' || xrayObj.barium_air_contrast === true}
                        onChange={() => handleCheckboxChange('barium_air_contrast')}
                    />
                </div>
                <Checkbox
                    label="Barium swallow/esophagram"
                    checked={xrayObj.barium_swallow === 'true' || xrayObj.barium_swallow === true}
                    onChange={() => handleCheckboxChange('barium_swallow')}
                />
                <Checkbox
                    label="with barium tablet"
                    checked={xrayObj.barium_tablet === 'true' || xrayObj.barium_tablet === true}
                    onChange={() => handleCheckboxChange('barium_tablet')}
                />
                <Checkbox
                    label="Small bowel follow-through (may take up to 4 hours)"
                    checked={xrayObj.small_bowel === 'true' || xrayObj.small_bowel === true}
                    onChange={() => handleCheckboxChange('small_bowel')}
                />
                <Checkbox
                    label="Upper GI"
                    checked={xrayObj.upper_gi === 'true' || xrayObj.upper_gi === true}
                    onChange={() => handleCheckboxChange('upper_gi')}
                />
                <Checkbox
                    label="Hysterosalpingogram (HSG)"
                    checked={xrayObj.hsg === 'true' || xrayObj.hsg === true}
                    onChange={() => handleCheckboxChange('hsg')}
                />
                <div className="flex flex-col sm:flex-row items-start md:items-center sm:space-x-4">
                    <Checkbox
                        label="Other"
                        className="md:mt-4"
                        checked={xrayObj.other === 'true' || xrayObj.other === true}
                        onChange={() => handleCheckboxChange('other')}
                    />
                    <Input
                        placeholder="Specify other"
                        value={xrayObj.other_specify || ""}
                        onChange={(e) => handleInputChange("other_specify", e.target.value)}
                    />
                </div>
            </div>
        </Card>
    );
});

export default XrayForm;
